import {
  AppLifecycleService,
  type IAppLifecycleService,
} from './core/lifecycle/AppLifecycleService'
import { BindHelper, DiLifetime } from './common/di/BindHelper'
import { CssThemeService } from './core/app-shell/CssThemeService'
import { NonLazyCreateService } from './core/lifecycle/NonLazyCreateService'
import { AuthService } from './core/auth/services/AuthService.ts'
import { GeneralConfig } from './core/configs/general-config'
import { SpacesService } from './spaces/services/SpacesService'
import { HttpClientConfig, HttpClientService } from './common/network/HttpClientService'
import { AuthStatusService } from './core/auth/services/AuthStatusService.ts'
import type { DiInitOptions, DomWindowAddedProperties } from './common/di/DomWindowAddedProperties'
import { type DiId } from './common/di/di-ids'
import './tsyringe-check'
import Bottle, { type IContainer } from 'bottlejs'
import { MultiBindingService } from './core/lifecycle/MultiBindingService'
import { SpacesApiService } from './spaces/services/SpacesApiService'
import { UsersService } from './user/UsersService'
import { UsersApiService } from './user/UsersApiService'
import { ObjectTypesApiService } from './object-types/services/ObjectTypesApiService'
import { ObjectTypesService } from './object-types/services/ObjectTypesService'
import { CollectionsApiService } from './objects/services/CollectionsApiService'
import { CollectionsTreeService } from './objects/services/CollectionsTreeService'
import { CollectionTreeController } from './objects/collections-tree/CollectionsTreeController'
import { ObjectsService } from './objects/services/ObjectsService'
import { ObjectsApiService } from './objects/services/ObjectsApiService'
import { CollectionViewsService } from './objects/services/CollectionViewsService'
import { NewItemButtonController } from './spaces/sidebar/selected-space-header/NewItemButtonController'
import { CreateObjectService } from './objects/services/CreateObjectService'
import { CreateObjectController } from './objects/create-object-page/CreateObjectController'
import { CreateTypeController } from './object-types/type-form/CreateTypeController'
import { EditTypeController } from './object-types/type-form/EditTypeController'
import { EditObjectController } from './objects/object-form/EditObjectController'
import { PropertyOptionsService } from './object-types/services/PropertyOptionService'
import { CollectionViewsApiService } from '@/objects/services/CollectionViewsApiService.ts'
import { GqlClientService } from '@/common/network/gql-client-service.ts'
import { CollectionObjectsService } from '@/objects/services/CollectionObjectsService.ts'
import { TutorialService } from '@/tutorial/TutorialService.ts'
import { CollectionTreeExpandedService } from '@/objects/services/CollectionTreeExpandedService.ts'
import { AppInfoApiService } from './app-info/AppInfoApiService'
import { AppInfoService } from './app-info/AppInfoService'
import { SearchApiService } from './search/SearchApiService'
import { SearchService } from './search/SearchService'
import { HotkeysService } from './core/hotkeys/HotkeysService'

const appBottle: Bottle<DiId> = init()
export const appContainer = appBottle.container as IContainer<DiId>

function init(): Bottle<DiId> {
  // console.log('APP INIT start', getTimeStr())
  const windowAddedProps = window as DomWindowAddedProperties
  // console.log(`playwrightTestArgs: ${JSON.stringify(windowAddedProps.playwrightTestArgs)}`)

  const { bottle, nonLazyCreateService } = createAppDiContainer(windowAddedProps.playwrightTestArgs)
  const container = bottle.container as IContainer<DiId>
  addContainerForTest(container)
  nonLazyCreateService.createAll()
  ;(container['IAppLifecycleService'] as IAppLifecycleService).emitOnInit()

  return bottle
}

function createAppDiContainer(diInitOptions?: DiInitOptions): {
  bottle: Bottle<DiId>
  nonLazyCreateService: NonLazyCreateService
} {
  const overrideMap = undefined

  const a = 42
  // diInitOptions?.serviceMockOverrides
  //   ? resolveOverrideMap(diInitOptions.serviceMockOverrides)
  //   : undefined

  const bottle = new Bottle<DiId>()
  const container = bottle.container

  const bindHelper = new BindHelper(bottle, overrideMap)

  bindHelper.bindSelf(CollectionViewsApiService, {
    dependencyIds: ['GqlClientService', 'ObjectTypesService'],
  })
  bindHelper.bindSelf(CollectionViewsService, {
    multiBindIds: ['INonLazyCreate'],
    dependencyIds: ['ObjectTypesService', 'CollectionViewsApiService', 'IAppLifecycleService'],
  })

  // container.bind(Container).toConstantValue(container)
  bindHelper.bindInstance(container, 'DiContainer')
  bindHelper.bindSelf(GeneralConfig)
  bindHelper.bindSelf(CssThemeService, {
    multiBindIds: ['INonLazyCreate'],
  })
  bindHelper.bind(AppLifecycleService, 'IAppLifecycleService')
  bindHelper.bindSelf(AuthService, {
    dependencyIds: ['DiContainer', 'AuthStatusService', 'IAppLifecycleService'],
  })
  bindHelper.bindSelf(AuthStatusService)
  bindHelper.bindSelf(GqlClientService, {
    dependencyIds: ['HttpClientService', 'AuthStatusService', 'AuthService'],
  })
  bindHelper.bindSelf(HttpClientService, {
    dependencyIds: ['GeneralConfig', 'HttpClientConfig', 'AuthService'],
  })
  bindHelper.bindSelf(HttpClientConfig)

  bindHelper.bindSelf(SpacesApiService, { dependencyIds: ['GqlClientService'] })
  bindHelper.bindSelf(SpacesService, {
    multiBindIds: ['INonLazyCreate'],
    dependencyIds: ['AuthService', 'GqlClientService', 'SpacesApiService', 'IAppLifecycleService'],
  })
  bindHelper.bindSelf(UsersApiService, { dependencyIds: ['GqlClientService'] })
  bindHelper.bindSelf(UsersService, {
    multiBindIds: ['INonLazyCreate'],
    dependencyIds: ['UsersApiService', 'IAppLifecycleService', 'AuthService'],
  })
  bindHelper.bindSelf(ObjectTypesApiService, { dependencyIds: ['GqlClientService'] })
  bindHelper.bindSelf(ObjectTypesService, {
    multiBindIds: ['INonLazyCreate'],
    dependencyIds: ['SpacesService', 'ObjectTypesApiService', 'PropertyOptionsService'],
  })
  bindHelper.bindSelf(CollectionsApiService, { dependencyIds: ['GqlClientService'] })
  bindHelper.bindSelf(CollectionsTreeService, {
    multiBindIds: ['INonLazyCreate'],
    dependencyIds: ['SpacesService', 'CollectionsApiService', 'ObjectTypesService'],
  })

  bindHelper.bindSelf(CollectionTreeController, {
    dependencyIds: ['CollectionsTreeService', 'SpacesService', 'CollectionViewsService'],
  })
  bindHelper.bindSelf(ObjectsService, {
    multiBindIds: ['INonLazyCreate'],
    dependencyIds: [
      'ObjectsApiService',
      'ObjectTypesService',
      'CollectionViewsService',
      'AuthService',
    ],
  })
  bindHelper.bindSelf(ObjectsApiService, {
    dependencyIds: ['GqlClientService', 'ObjectTypesService'],
  })
  bindHelper.bindSelf(CollectionObjectsService, {
    dependencyIds: [
      'ObjectsService',
      'CollectionViewsService',
      'CollectionsTreeService',
      'ObjectTypesService',
    ],
  })

  bindHelper.bindSelf(NewItemButtonController, {
    dependencyIds: ['ObjectTypesService'],
    lifetime: DiLifetime.Transient,
  })
  bindHelper.bindSelf(CreateObjectService, {})
  bindHelper.bindSelf(CreateObjectController, {
    dependencyIds: ['ObjectsService'],
    lifetime: DiLifetime.Transient,
  })
  bindHelper.bindSelf(CreateTypeController, {
    dependencyIds: ['ObjectTypesService'],
    lifetime: DiLifetime.Transient,
  })
  bindHelper.bindSelf(EditTypeController, {
    dependencyIds: ['ObjectTypesService'],
    lifetime: DiLifetime.Transient,
  })
  bindHelper.bindSelf(EditObjectController, {
    dependencyIds: ['ObjectsService'],
    lifetime: DiLifetime.Transient,
  })
  bindHelper.bindSelf(PropertyOptionsService, {})

  bindHelper.bindSelf(TutorialService, {
    multiBindIds: ['INonLazyCreate'],
    dependencyIds: ['UsersService', 'UsersApiService'],
  })
  bindHelper.bindSelf(CollectionTreeExpandedService, {
    dependencyIds: ['IAppLifecycleService'],
    multiBindIds: ['INonLazyCreate'],
  })
  bindHelper.bindSelf(AppInfoApiService, { dependencyIds: ['HttpClientService'] })
  bindHelper.bindSelf(AppInfoService, {
    multiBindIds: ['INonLazyCreate'],
    dependencyIds: ['AppInfoApiService', 'IAppLifecycleService'],
  })

  bindHelper.bindSelf(SearchApiService, { dependencyIds: ['GqlClientService'] })
  bindHelper.bindSelf(SearchService, { dependencyIds: ['SearchApiService'] })
  bindHelper.bindSelf(HotkeysService)

  const multiBindingService = new MultiBindingService(bindHelper.multiIdToIds)
  bindHelper.bindInstance(multiBindingService, 'MultiBindingService')

  const nonLazyCreateService = new NonLazyCreateService(
    bottle.container as IContainer<DiId>,
    multiBindingService,
  )

  return { bottle, nonLazyCreateService }
}

function addContainerForTest(container: IContainer<DiId>) {
  if (window.location.hostname === 'localhost') {
    ;(window as DomWindowAddedProperties).appContainer = container
  }
}
